import { useState, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Client, Comment } from "../../../utils/types";

import { saveClientNote } from '../../../utils/helper';
import { CommentBox } from '../CommentBox/CommentBox';

import { useAuth } from '../../../context/AuthContext';

type CommentsCardProps = {
  selectedClient: Client;
}


export const CommentsCard = (props: CommentsCardProps) => {
  const { jwt } = useAuth()

  const queryClient = useQueryClient();
  
  const { selectedClient } = props;

  const [newComment, setNewComment] = useState("");
  const comments: Comment[] | undefined = selectedClient?.clientNotes
  const commentsBoxRef = useRef<HTMLDivElement>(null);

  const addClientNoteMutation = useMutation({
    mutationFn: ([clientId, note]: [string, Comment]) => saveClientNote(clientId, note, jwt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const handleSaveComment = (clientId: string, note: Comment) => {
    addClientNoteMutation.mutateAsync([clientId, note]);
  };

  useEffect(() => {
    if (commentsBoxRef.current) {
      commentsBoxRef.current.scrollTop = commentsBoxRef.current.scrollHeight;
    }
  }, [selectedClient]);

  return (
    <Card
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          pb: 0,
        }}
      >
        <Typography variant="h5" sx={{ mb: 1 }}>
          {selectedClient?.clientName || "Select a client"}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Comments
        </Typography>
        <Box
          ref={commentsBoxRef}
          sx={{
            flex: 1,
            minHeight: 120,
            maxHeight: 240, // Add a maxHeight to prevent full height
            overflowY: "auto",
            borderRadius: 1,
            p: 1,
            mb: 2,
            width: "100%", // Ensure it doesn't overflow horizontally
            boxSizing: "border-box", // Prevents horizontal scrollbars
          }}
        >
          {comments && comments.length > 0 ? (
            <CommentBox
              comments={[...comments].sort((a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime())}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No comments yet.
            </Typography>
          )}
        </Box>
      </CardContent>
      <Box sx={{ display: "flex", gap: 1, mb: 2, mt: "auto", px: 2 }}>
        <TextField
          label="Add a comment"
          size="small"
          fullWidth
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          disabled={!selectedClient}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSaveComment(selectedClient.id as string, { note: newComment });
              setNewComment("");
            }
          }}
        />
        <Button
          variant="outlined"
          disabled={!newComment || !selectedClient}
          onClick={() => {
            handleSaveComment(selectedClient.id as string, { note: newComment });
            setNewComment("");
          }}
        >
          Add
        </Button>
      </Box>
    </Card>
  )
}